class YuanVariantGrid extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.quantities = new Map();
    this.variants = this.parseVariants();
    this.activeRow = this.querySelector('[data-yuan-variant]');

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('input', this.onInput.bind(this));

    if (this.querySelector('[data-yuan-wishlist-toggle]')) this.restoreWishlist();
    this.syncFromOptions({ preserveInput: true });
    this.isolateShippingNumbers();
    this.updateSummary();
  }

  parseVariants() {
    const node = this.previousElementSibling?.matches?.('[data-yuan-variants-json]')
      ? this.previousElementSibling
      : this.parentElement?.querySelector('[data-yuan-variants-json]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Yuan variant grid: invalid variants JSON', error);
      return [];
    }
  }

  onClick(event) {
    const quantityButton = event.target.closest('[data-yuan-quantity]');
    if (quantityButton) {
      this.changeQuantity(quantityButton);
      return;
    }

    if (event.target.closest('[data-yuan-wishlist-toggle]')) {
      this.toggleWishlist();
      return;
    }

    if (event.target.closest('[data-yuan-buy-now]')) {
      this.buyNow();
      return;
    }

    if (event.target.closest('[data-yuan-lead-toggle]')) {
      this.toggleLead();
      return;
    }

    if (event.target.closest('[data-yuan-add]')) this.addSelectedVariants();
  }

  toggleLead() {
    const wrap = this.querySelector('[data-yuan-lead]');
    const button = wrap?.querySelector('[data-yuan-lead-toggle]');
    if (!wrap || !button) return;
    const expanded = wrap.getAttribute('aria-expanded') === 'true';
    wrap.setAttribute('aria-expanded', String(!expanded));
    button.textContent = expanded ? this.dataset.readMoreLabel : this.dataset.readLessLabel;
  }

  onChange(event) {
    if (event.target.matches('[data-yuan-option]')) {
      this.syncFromOptions();
      return;
    }

    if (event.target.matches('[data-yuan-variant] input, [data-yuan-selected-item] input')) {
      this.normalizeInput(event.target);
      this.persistInput(event.target);
      this.renderSelectedList();
      this.updateSummary();
    }
  }

  onInput(event) {
    if (!event.target.matches('[data-yuan-variant] input, [data-yuan-selected-item] input')) return;
    this.persistInput(event.target);
    this.updateSummary();
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-yuan-option]')].map((select) => select.value);
  }

  findVariant(options) {
    if (!options.length) return this.variants[0] || null;
    return (
      this.variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === options.length &&
          variant.options.every((value, index) => value === options[index])
      ) || null
    );
  }

  syncFromOptions({ preserveInput = false } = {}) {
    const options = this.selectedOptions();
    const variant = this.findVariant(options) || this.variants[0];
    if (!variant || !this.activeRow) return;

    this.applyVariantToActive(variant, { preserveInput });
    this.syncProductGallery(variant);
    this.refreshOptionAvailability(options);
    this.renderSelectedList();
    this.updateSummary();
  }

  applyVariantToActive(variant, { preserveInput = false } = {}) {
    const input = this.activeRow.querySelector('input');
    const previousId = this.activeRow.dataset.variantId;
    if (!preserveInput && previousId) {
      this.quantities.set(String(previousId), Number(input.value) || 0);
    }

    this.activeRow.dataset.variantId = String(variant.id);
    this.activeRow.dataset.price = String(variant.price);
    this.activeRow.classList.toggle('is-unavailable', !variant.available);

    const title = this.activeRow.querySelector('[data-yuan-active-title]');
    const status = this.activeRow.querySelector('[data-yuan-active-status]');
    const priceLabel = this.activeRow.querySelector('[data-yuan-active-price-label]');
    const basePrice = this.querySelector('[data-yuan-active-price]');
    const media = this.activeRow.querySelector('[data-yuan-active-media]');

    if (title) title.textContent = variant.title;
    if (priceLabel) priceLabel.textContent = this.formatMoney(variant.price / 100);
    if (basePrice) {
      basePrice.textContent = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      }).format(variant.price / 100);
    }
    if (status) {
      status.textContent = variant.available ? this.dataset.readyLabel : this.dataset.unavailableLabel;
    }

    if (media) {
      // Replace the node so leftover Shopify srcset/sizes cannot keep the old color image.
      if (variant.image) {
        const img = document.createElement('img');
        img.src = variant.image;
        img.alt = variant.title || '';
        img.loading = 'lazy';
        img.width = 48;
        img.height = 48;
        media.replaceChildren(img);
      } else {
        media.replaceChildren();
      }
    }

    input.step = String(variant.step || 1);
    input.dataset.minimum = String(variant.minimum || variant.step || 1);
    if (variant.maximum != null) input.max = String(variant.maximum);
    else input.removeAttribute('max');
    input.disabled = !variant.available;
    this.activeRow.querySelectorAll('[data-yuan-quantity]').forEach((button) => {
      button.disabled = !variant.available;
    });

    const stored = this.quantities.get(String(variant.id));
    input.value = String(variant.available ? stored || 0 : 0);
    if (variant.available) this.normalizeInput(input);
  }

  syncProductGallery(variant) {
    if (!variant?.mediaId) return;

    const productInfo = this.closest('product-info');
    const gallery = productInfo?.querySelector('media-gallery');
    const sectionId = productInfo?.dataset.section;
    if (!gallery || typeof gallery.setActiveMedia !== 'function' || !sectionId) return;

    gallery.setActiveMedia(`${sectionId}-${variant.mediaId}`, true);

    const productUrl = productInfo.dataset.url;
    if (productInfo.dataset.updateUrl !== 'false' && productUrl && variant.id) {
      window.history.replaceState({}, '', `${productUrl}?variant=${variant.id}`);
    }
  }

  refreshOptionAvailability(currentOptions) {
    this.querySelectorAll('[data-yuan-option]').forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex);
      [...select.options].forEach((optionEl) => {
        const trial = currentOptions.slice();
        trial[optionIndex] = optionEl.value;
        const match = this.findVariant(trial);
        const available = Boolean(match?.available);
        optionEl.disabled = !match;
        optionEl.dataset.available = String(available);
      });
    });
  }

  changeQuantity(button) {
    const row = button.closest('[data-yuan-variant], [data-yuan-selected-item]');
    const input = row?.querySelector('input');
    if (!input || input.disabled) return;

    const current = Number(input.value) || 0;
    const step = Number(input.step) || 1;
    const minimum = Number(input.dataset.minimum) || step;
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    let nextValue;

    if (button.dataset.yuanQuantity === 'increase') {
      nextValue = current === 0 ? minimum : current + step;
      nextValue = Math.min(nextValue, maximum);
    } else {
      nextValue = current <= minimum ? 0 : current - step;
    }

    input.value = String(Math.max(0, nextValue));
    this.persistInput(input);
    this.syncActiveInputFromMap();
    this.renderSelectedList();
    this.updateSummary();
  }

  persistInput(input) {
    const row = input.closest('[data-yuan-variant], [data-yuan-selected-item]');
    if (!row?.dataset.variantId) return;
    this.quantities.set(String(row.dataset.variantId), Number(input.value) || 0);
  }

  syncActiveInputFromMap() {
    if (!this.activeRow) return;
    const input = this.activeRow.querySelector('input');
    const stored = this.quantities.get(String(this.activeRow.dataset.variantId));
    if (stored == null) return;
    input.value = String(stored);
  }

  normalizeInput(input) {
    const step = Number(input.step) || 1;
    const minimum = Number(input.dataset.minimum) || step;
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const requested = Math.max(0, Number(input.value) || 0);

    if (requested === 0) {
      input.value = '0';
      return;
    }

    const normalized = Math.max(minimum, Math.ceil(requested / step) * step);
    input.value = String(Math.min(normalized, maximum));
  }

  selectedItems() {
    if (!this.activeRow) return [];
    const id = this.activeRow.dataset.variantId;
    const quantity = Number(this.activeRow.querySelector('input')?.value) || 0;
    if (!id || quantity <= 0) return [];

    const variant = this.variants.find((item) => String(item.id) === String(id));
    return [
      {
        id: Number(id),
        quantity,
        price: Number(variant?.price || this.activeRow.dataset.price) || 0,
        title: variant?.title || '',
        priceLabel: variant?.priceLabel || '',
        image: variant?.image || null,
        step: variant?.step || 1,
        minimum: variant?.minimum || 1,
        maximum: variant?.maximum,
        available: Boolean(variant?.available),
      },
    ];
  }

  renderSelectedList() {
    /* Selected-quantities list removed from UI */
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  updateSummary() {
    const selectedVariants = this.selectedItems();
    const quantity = selectedVariants.reduce((total, variant) => total + variant.quantity, 0);
    const totalPrice =
      selectedVariants.reduce((total, variant) => total + variant.quantity * variant.price, 0) / 100;
    const button = this.querySelector('[data-yuan-add]');
    const buttonText = this.querySelector('[data-yuan-add-text]');
    const buyNow = this.querySelector('[data-yuan-buy-now]');
    const selection = this.querySelector('[data-yuan-selection]');

    /*
     * An empty selection is marked with aria-disabled rather than the disabled
     * property: a disabled button fires no click, and these buttons need to
     * respond by pointing the shopper at the quantity rows.
     */
    const isEmpty = quantity === 0;
    /*
     * The in-flight guard uses the real disabled property, so clear it here:
     * this method is what returns the buttons to their resting state.
     */
    button.disabled = false;
    if (buyNow) buyNow.disabled = false;
    button.setAttribute('aria-disabled', String(isEmpty));
    buttonText.textContent = isEmpty
      ? this.dataset.emptyLabel
      : `${this.dataset.addLabel} — ${this.formatMoney(totalPrice)}`;
    if (buyNow) buyNow.setAttribute('aria-disabled', String(isEmpty));
    selection.textContent = quantity > 0 ? this.formatSelection(quantity) : '';

    this.dataset.hasSelection = String(!isEmpty);
    if (!isEmpty) this.hidePrompt();
  }

  /*
   * Draws attention to the quantity rows when a buy action is attempted with
   * nothing selected. Called from both the inline buttons and the sticky bar.
   */
  promptForQuantity() {
    const prompt = this.querySelector('[data-yuan-prompt]');
    const target = this.querySelector('[data-yuan-variant]') || this;

    target.scrollIntoView({
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });

    if (!prompt) return;
    prompt.hidden = false;
    prompt.classList.remove('is-flash');
    // Restart the animation on repeat presses.
    void prompt.offsetWidth;
    prompt.classList.add('is-flash');

    clearTimeout(this.promptTimer);
    this.promptTimer = setTimeout(() => this.hidePrompt(), 6000);
  }

  hidePrompt() {
    const prompt = this.querySelector('[data-yuan-prompt]');
    if (!prompt) return;
    clearTimeout(this.promptTimer);
    prompt.hidden = true;
    prompt.classList.remove('is-flash');
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  hasSelection() {
    return this.selectedItems().length > 0;
  }

  formatMoney(amount) {
    const formatted = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(amount);
    return `${formatted} ${this.dataset.currencySuffix || this.dataset.currency || 'EGP'}`;
  }

  formatSelection(quantity) {
    return new Intl.NumberFormat(document.documentElement.lang || 'ar-EG').format(quantity);
  }

  isolateShippingNumbers() {
    const label = this.querySelector('[data-yuan-shipping-label]');
    if (!label) return;

    const digits = '٠١٢٣٤٥٦٧٨٩۰۱۲۳۴۵۶۷۸۹';
    const latin = label.textContent.replace(/[٠-٩۰-۹]/g, (digit) => String(digits.indexOf(digit) % 10));
    const parts = latin.split(/([0-9]+(?:[.,\-–—/][0-9]+)*)/g);
    label.replaceChildren(...parts.map((part, index) => {
      if (index % 2 === 0) return document.createTextNode(part);
      const number = document.createElement('bdi');
      number.dir = 'ltr';
      number.textContent = part;
      return number;
    }));
  }
  async buyNow() {
    const selectedVariants = this.selectedItems().map(({ id, quantity }) => ({ id, quantity }));
    if (selectedVariants.length === 0) {
      this.promptForQuantity();
      return;
    }
    const button = this.querySelector('[data-yuan-buy-now]');
    const message = this.querySelector('[data-yuan-message]');
    button.disabled = true;
    button.classList.add('is-loading');
    message.hidden = true;
    message.classList.remove('is-success');
    try {
      await this.requestCartAdd(selectedVariants, null);
      const root = window.Shopify?.routes?.root || '/';
      window.location.href = `${root}checkout`;
    } catch (error) {
      message.textContent = error.message || window.cartStrings?.error || error.toString();
      message.hidden = false;
      button.classList.remove('is-loading');
      this.updateSummary();
    }
  }

  async addSelectedVariants() {
    const selectedVariants = this.selectedItems().map(({ id, quantity }) => ({ id, quantity }));
    if (selectedVariants.length === 0) {
      this.promptForQuantity();
      return;
    }
    const button = this.querySelector('[data-yuan-add]');
    const message = this.querySelector('[data-yuan-message]');
    const cartSurface = this.cartSurface();
    button.disabled = true;
    button.classList.add('is-loading');
    message.hidden = true;
    message.classList.remove('is-success');
    try {
      const cartResponse = await this.requestCartAdd(selectedVariants, cartSurface);
      this.resetQuantities();
      this.showCartResponse(cartResponse, cartSurface);
    } catch (error) {
      message.textContent = error.message || window.cartStrings?.error || error.toString();
      message.hidden = false;
    } finally {
      button.classList.remove('is-loading');
      this.updateSummary();
    }
  }

  cartSurface() {
    return document.querySelector('cart-drawer');
  }

  async requestCartAdd(selectedVariants, cartSurface) {
    const requestBody = { items: selectedVariants };
    if (cartSurface) {
      requestBody.sections = cartSurface.getSectionsToRender().map((section) => section.id);
      requestBody.sections_url = window.location.pathname;
      cartSurface.setActiveElement(document.activeElement);
    } else {
      requestBody.sections = ['cart-icon-bubble'];
      requestBody.sections_url = window.location.pathname;
    }
    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(requestBody),
    });
    const cartResponse = await response.json();
    if (!response.ok || cartResponse.status) {
      throw new Error(cartResponse.description || cartResponse.message || window.cartStrings?.error);
    }
    return cartResponse;
  }

  resetQuantities() {
    this.quantities.clear();
    this.querySelectorAll('[data-yuan-variant] input').forEach((input) => {
      input.value = '0';
    });
    this.renderSelectedList();
    this.updateSummary();
  }

  showCartResponse(cartResponse, cartSurface) {
    if (cartSurface) {
      cartSurface.classList.remove('is-empty');
      cartSurface.renderContents(cartResponse);
      return;
    }
    this.updateCartIcon(cartResponse.sections['cart-icon-bubble']);
    const message = this.querySelector('[data-yuan-message]');
    message.textContent = this.dataset.successLabel;
    message.classList.add('is-success');
    message.hidden = false;
  }

  updateCartIcon(sectionHtml) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    const sectionRoot = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
    if (!cartIcon || !sectionRoot) {
      window.location.assign(routes.cart_url);
      return;
    }
    cartIcon.innerHTML = sectionRoot.innerHTML;
  }

  wishlistKey() {
    return `yuan-wishlist-${this.dataset.productId}`;
  }

  restoreWishlist() {
    try {
      this.setWishlistState(localStorage.getItem(this.wishlistKey()) === 'true');
    } catch (storageError) {
      if (!(storageError instanceof DOMException)) throw storageError;
      this.setWishlistState(false);
    }
  }

  toggleWishlist() {
    const button = this.querySelector('[data-yuan-wishlist-toggle]');
    const nextState = button.getAttribute('aria-pressed') !== 'true';
    this.setWishlistState(nextState);
    try {
      localStorage.setItem(this.wishlistKey(), String(nextState));
    } catch (storageError) {
      if (!(storageError instanceof DOMException)) throw storageError;
    }
  }

  setWishlistState(active) {
    const button = this.querySelector('[data-yuan-wishlist-toggle]');
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? this.dataset.wishlistRemove : this.dataset.wishlistAdd);
  }
}

if (!customElements.get('yuan-variant-grid')) {
  customElements.define('yuan-variant-grid', YuanVariantGrid);
}

/*
 * Sticky product bar.
 *
 * Its buy controls live outside <yuan-variant-grid>, so they are wired here as
 * thin triggers that delegate to the grid. That keeps quantity state, cart
 * calls and the empty-selection prompt in one place.
 */
class YuanStickyBar {
  constructor(bar) {
    this.bar = bar;
    this.grid = document.querySelector('yuan-variant-grid');
    if (!this.grid) return;

    this.onClick = this.onClick.bind(this);
    this.bar.addEventListener('click', this.onClick);
    this.actions = this.bar.querySelector('.yuan-sticky-bar__actions');
    this.actions.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'max-height') this.publishHeight();
    });

    // Reserve room at the end of the page so the bar never covers the footer.
    document.body.classList.add('yuan-has-sticky-bar');
    this.bar.classList.add('is-visible');

    this.publishHeight();
    this.watchInlineActions();
    window.addEventListener('resize', () => this.publishHeight(), { passive: true });
  }

  onClick(event) {
    const buy = event.target.closest('[data-yuan-sticky-buy]');
    const add = event.target.closest('[data-yuan-sticky-add]');
    if (!buy && !add) return;

    event.preventDefault();

    // With nothing chosen, both actions send the shopper to the rows instead.
    if (!this.grid.hasSelection()) {
      this.grid.promptForQuantity();
      return;
    }

    if (buy) this.grid.buyNow();
    else this.grid.addSelectedVariants();
  }

  /* Reserve page space so the bar never covers the end of the content. */
  publishHeight() {
    const height = this.bar.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--yuan-sticky-bar-height', `${Math.ceil(height)}px`);
  }

  watchInlineActions() {
    const targets = this.grid.querySelectorAll('[data-yuan-add], [data-yuan-buy-now]');
    if (!targets.length || typeof IntersectionObserver === 'undefined') return;

    const visible = new Set();
    const syncStickyBuyButtons = () => {
      const inlineActionsVisible = visible.size > 0;
      this.bar.classList.toggle('is-inline-actions-visible', inlineActionsVisible);
      this.actions.toggleAttribute('inert', inlineActionsVisible);
      this.actions.setAttribute('aria-hidden', String(inlineActionsVisible));
      this.publishHeight();
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      syncStickyBuyButtons();
    });

    targets.forEach((button) => observer.observe(button));
  }
}

function initYuanStickyBar() {
  document.querySelectorAll('[data-yuan-sticky-bar]').forEach((bar) => {
    if (bar.dataset.ready === 'true') return;
    bar.dataset.ready = 'true';
    new YuanStickyBar(bar);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initYuanStickyBar);
} else {
  initYuanStickyBar();
}

// The theme editor re-renders sections without a full page load.
document.addEventListener('shopify:section:load', initYuanStickyBar);
